import { useQuery } from '@tanstack/react-query'
import { apiClient, getSessionTokenHeaders } from '../client'
import { useEnrollmentStore } from '@/stores'
import type { EndorsementOption } from '../types/enrollment'
import { BASE_URL } from './shared'
import type { ContactFieldsData, ContactFieldOption } from './types'

const toTitleCaseLabel = (label: string) =>
  label.replace(/\b([a-z])/gi, char => char.toUpperCase())

type RawContactFieldsResponse = {
  gender?: ContactFieldOption[]
  ethnicity?: ContactFieldOption[]
  levelOfEducation?: ContactFieldOption[]
}

export const useEndorsements = () => {
  const sessionToken = useEnrollmentStore.getState().sessionToken?.trim() ?? ''

  return useQuery({
    queryKey: ['enrollment', 'endorsements', sessionToken],
    queryFn: async () => {
      if (!sessionToken) {
        return []
      }

      const response = await apiClient.get<EndorsementOption[]>(
        `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/reference/endorsements`,
        {
          headers: getSessionTokenHeaders(sessionToken)
        }
      )

      return response.data
    },
    enabled: Boolean(sessionToken),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always'
  })
}

export const useContactFields = () => {
  const storedSessionToken = useEnrollmentStore(state => state.sessionToken)
  const sessionToken = storedSessionToken?.trim() ?? ''

  return useQuery({
    queryKey: ['enrollment', 'reference', 'contact-fields', sessionToken],
    queryFn: async () => {
      const response = await apiClient.get<RawContactFieldsResponse>(
        `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/reference/contact-fields`,
        {
          headers: getSessionTokenHeaders(sessionToken)
        }
      )
      const raw = response.data

      return {
        gender: raw.gender ?? [],
        ethnicity: (raw.ethnicity ?? []).map(option => ({
          ...option,
          label: toTitleCaseLabel(option.label)
        })),
        levelOfEducation: raw.levelOfEducation ?? []
      } satisfies ContactFieldsData
    },
    enabled: Boolean(sessionToken),
    staleTime: Infinity
  })
}
